// Real-time task notification bell + modal. Self-contained replacement for
// the generic `<NotificationBell><TaskNotificationsPanel/></NotificationBell>`
// combo used in the sidebar.
//
// Renders:
//   - animated bell button with unread badge + shake on arrival
//   - modal overlay with forest-gradient header, sound toggle, Unread/All pill
//   - task-grouped rows under Today / Yesterday / Earlier sections
//   - inline actions: mark read, mute task, open task, delete
//
// Hydrates from REST (`tasksApi.getNotifications` + `getMutedTasks`) on view-
// mode change. Live WS frames arrive via `useGlobalNotificationsWs` (mounted
// in the sidebar) and dispatch `pushNotification` directly to Redux.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as Popover from '@radix-ui/react-popover';
import {
  AlertTriangle,
  AtSign,
  Ban,
  Bell,
  BellOff,
  Check,
  CheckCheck,
  ChevronDown,
  ClipboardList,
  Clock,
  ExternalLink,
  Eye,
  MessageSquare,
  RefreshCw,
  Sparkles,
  Trash2,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { tasksApi, TaskNotification } from '../../hrms/api/tasks';
import { useAuth } from '../../contexts/AuthContext';
import {
  bulkHydrate,
  clearNotifications,
  markRead,
  markAllRead,
  removeNotification,
  removeAllNotifications,
  selectNotifications,
  selectNotificationsHydrated,
  selectUnreadCount,
} from '../../redux/slices/notifications';
import {
  getSoundEnabled,
  setSoundEnabled,
  previewNotificationSound,
} from '../../utils/taskNotificationSound';

type ViewMode = 'unread' | 'all';
const VIEW_MODE_KEY = 'notif:viewMode';
function getStoredViewMode(): ViewMode {
  if (typeof window === 'undefined') return 'unread';
  return window.localStorage.getItem(VIEW_MODE_KEY) === 'all' ? 'all' : 'unread';
}

interface KindMeta {
  Icon: LucideIcon;
  label: string;
  tileBg: string;
  tileFg: string;
  accent: string;
}

const KIND_META: Record<string, KindMeta> = {
  ASSIGNED: {
    Icon: ClipboardList,
    label: 'Assigned',
    tileBg: '#d1fae5',
    tileFg: '#065f46',
    accent: '#10b981',
  },
  MENTIONED: {
    Icon: AtSign,
    label: 'Mentioned',
    tileBg: '#dbeafe',
    tileFg: '#1e40af',
    accent: '#3b82f6',
  },
  STATUS: {
    Icon: RefreshCw,
    label: 'Status',
    tileBg: '#fde68a',
    tileFg: '#92400e',
    accent: '#f59e0b',
  },
  BLOCKED: {
    Icon: Ban,
    label: 'Hold',
    tileBg: '#fecaca',
    tileFg: '#991b1b',
    accent: '#ef4444',
  },
  COMMENT: {
    Icon: MessageSquare,
    label: 'Comment',
    tileBg: '#c7d2fe',
    tileFg: '#3730a3',
    accent: '#6366f1',
  },
  COMPLETED: {
    Icon: CheckCheck,
    label: 'Completed',
    tileBg: '#a7f3d0',
    tileFg: '#065f46',
    accent: '#10b981',
  },
  DUE_SOON: {
    Icon: Clock,
    label: 'Due soon',
    tileBg: '#fed7aa',
    tileFg: '#9a3412',
    accent: '#f97316',
  },
  OVERDUE: {
    Icon: AlertTriangle,
    label: 'Overdue',
    tileBg: '#fca5a5',
    tileFg: '#7f1d1d',
    accent: '#dc2626',
  },
  REVIEW_REQUESTED: {
    Icon: Eye,
    label: 'Review',
    tileBg: '#ddd6fe',
    tileFg: '#5b21b6',
    accent: '#8b5cf6',
  },
};

const FALLBACK_META: KindMeta = {
  Icon: Sparkles,
  label: 'Update',
  tileBg: '#e5e7eb',
  tileFg: '#374151',
  accent: '#6b7280',
};

function metaFor(kind: string): KindMeta {
  return KIND_META[kind] ?? FALLBACK_META;
}

interface ActionButtonProps {
  title: string;
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  danger?: boolean;
  active?: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({ title, onClick, children, danger, active }) => {
  const base: React.CSSProperties = {
    background: active ? 'var(--gt-prio-urgent-bg, #fee2e2)' : 'transparent',
    color: active ? 'var(--gt-prio-urgent-fg, #991b1b)' : 'var(--gt-text-tertiary, #6b7280)',
    transition: 'background 120ms, color 120ms',
  };
  return (
    <button
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      className="p-1.5 rounded-lg"
      style={base}
      onMouseEnter={(e) => {
        const el = e.currentTarget;
        el.style.background = danger
          ? 'var(--gt-prio-urgent-bg, #fee2e2)'
          : 'var(--gt-surface-2, #f3f4f6)';
        el.style.color = danger
          ? 'var(--gt-prio-urgent-fg, #991b1b)'
          : 'var(--gt-text-primary, #111827)';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget;
        el.style.background = active ? 'var(--gt-prio-urgent-bg, #fee2e2)' : 'transparent';
        el.style.color = active
          ? 'var(--gt-prio-urgent-fg, #991b1b)'
          : 'var(--gt-text-tertiary, #6b7280)';
      }}
    >
      {children}
    </button>
  );
};

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const diff = Date.now() - date.getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(diff / 3_600_000);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(diff / 86_400_000);
  if (d < 7) return `${d}d ago`;
  return date.toLocaleDateString();
}

type TimeBucket = 'today' | 'yesterday' | 'earlier';

function bucketFor(dateStr: string): TimeBucket {
  const d = new Date(dateStr);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 86_400_000;
  const t = d.getTime();
  if (t >= startOfToday) return 'today';
  if (t >= startOfYesterday) return 'yesterday';
  return 'earlier';
}

interface TaskGroup {
  task_id: string;
  items: TaskNotification[];
  latest: TaskNotification;
  hasUnread: boolean;
}

function groupByTask(items: TaskNotification[]): TaskGroup[] {
  const map = new Map<string, TaskGroup>();
  for (const n of items) {
    let g = map.get(n.task_id);
    if (!g) {
      g = { task_id: n.task_id, items: [], latest: n, hasUnread: false };
      map.set(n.task_id, g);
    }
    g.items.push(n);
    if (!n.is_read) g.hasUnread = true;
    if (new Date(n.created_at).getTime() > new Date(g.latest.created_at).getTime()) {
      g.latest = n;
    }
  }
  return Array.from(map.values()).sort(
    (a, b) => new Date(b.latest.created_at).getTime() - new Date(a.latest.created_at).getTime()
  );
}

export function TaskNotificationsBell() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const userId = user?.id || null;

  const allNotifications = useSelector(selectNotifications);
  const unreadCount = useSelector(selectUnreadCount);
  const hydrated = useSelector(selectNotificationsHydrated);

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [mutedIds, setMutedIds] = useState<Set<string>>(new Set());
  const [soundOn, setSoundOnState] = useState<boolean>(getSoundEnabled());
  const [animating, setAnimating] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(getStoredViewMode);
  const prevUnreadRef = useRef(unreadCount);
  const bodyInnerRef = useRef<HTMLDivElement>(null);
  const [bodyHeight, setBodyHeight] = useState<number | null>(null);

  const items = useMemo(
    () => (viewMode === 'unread' ? allNotifications.filter((n) => !n.is_read) : allNotifications),
    [allNotifications, viewMode]
  );

  useEffect(() => {
    if (!userId) {
      dispatch(clearNotifications());
      return;
    }
    let cancelled = false;
    setLoading(true);
    const unreadOnly = viewMode === 'unread';
    Promise.all([
      tasksApi.getNotifications(unreadOnly, 50),
      tasksApi.getMutedTasks().catch(() => ({ data: { task_ids: [] as string[] } })),
    ])
      .then(([nRes, mRes]: any) => {
        if (cancelled) return;
        const data = Array.isArray(nRes?.data) ? nRes.data : nRes?.data?.items ?? [];
        dispatch(bulkHydrate(data));
        setMutedIds(new Set(mRes.data?.task_ids ?? []));
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to load notifications:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, userId, viewMode]);

  const changeViewMode = (mode: ViewMode) => {
    if (mode === viewMode) return;
    setViewMode(mode);
    if (typeof window !== 'undefined') window.localStorage.setItem(VIEW_MODE_KEY, mode);
  };

  // Pulse the icon when a new notification arrives.
  useEffect(() => {
    if (unreadCount > prevUnreadRef.current) {
      setAnimating(true);
      const t = setTimeout(() => setAnimating(false), 900);
      return () => clearTimeout(t);
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount]);

  const groups = useMemo(() => groupByTask(items), [items]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setBodyHeight(null);
      return;
    }
    const el = bodyInnerRef.current;
    if (!el) return;
    const cap = Math.floor(window.innerHeight * 0.62);
    const next = Math.min(el.scrollHeight, cap);
    setBodyHeight((prev) => (prev === next ? prev : next));
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const inner = bodyInnerRef.current;
      if (!inner) return;
      const target = Math.min(inner.scrollHeight, Math.floor(window.innerHeight * 0.62));
      setBodyHeight((prev) => (prev === target ? prev : target));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen, groups, viewMode, loading, expanded]);

  const bucketed = useMemo(() => {
    const buckets: Record<TimeBucket, TaskGroup[]> = { today: [], yesterday: [], earlier: [] };
    for (const g of groups) buckets[bucketFor(g.latest.created_at)].push(g);
    return buckets;
  }, [groups]);

  const handleMarkRead = async (id: string) => {
    dispatch(markRead(id));
    try {
      await tasksApi.markNotificationRead(id);
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    dispatch(markAllRead());
    try {
      await tasksApi.markAllNotificationsRead();
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleDelete = async (notificationId: string) => {
    dispatch(removeNotification(notificationId));
    try {
      await tasksApi.deleteNotification(notificationId);
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const handleDeleteGroup = async (taskGroup: TaskGroup) => {
    const ids = taskGroup.items.map((n) => n.id);
    ids.forEach((id) => dispatch(removeNotification(id)));
    await Promise.allSettled(ids.map((id) => tasksApi.deleteNotification(id)));
  };

  const handleClearAll = async () => {
    if (
      typeof window !== 'undefined' &&
      !window.confirm('Delete all notifications? This cannot be undone.')
    ) {
      return;
    }
    dispatch(removeAllNotifications());
    try {
      await tasksApi.deleteAllNotifications();
    } catch (err) {
      console.error('Failed to clear all:', err);
    }
  };

  const handleOpen = (taskId: string, notifId?: string) => {
    if (notifId) handleMarkRead(notifId);
    navigate(`/tasks/${taskId}`);
    setIsOpen(false);
  };

  const handleToggleMute = async (taskId: string) => {
    setMutedIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
    try {
      const res = await tasksApi.toggleTaskMute(taskId);
      setMutedIds((prev) => {
        const next = new Set(prev);
        if (res.data.muted) next.add(taskId);
        else next.delete(taskId);
        return next;
      });
    } catch (err) {
      setMutedIds((prev) => {
        const next = new Set(prev);
        if (next.has(taskId)) next.delete(taskId);
        else next.add(taskId);
        return next;
      });
      console.error('Failed to toggle mute:', err);
    }
  };

  const toggleExpand = (taskId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOnState(next);
    setSoundEnabled(next);
    if (next) previewNotificationSound();
  };

  const staggerRef = { i: 0 };
  const nextStagger = () => Math.min(staggerRef.i++, 10);

  const renderGroup = (g: TaskGroup) => {
    const meta = metaFor(g.latest.kind);
    const muted = mutedIds.has(g.task_id);
    const isExpanded = expanded.has(g.task_id);
    const count = g.items.length;
    const Icon = meta.Icon;
    const stagger = nextStagger();
    return (
      <div
        key={g.task_id}
        className="group/n relative gt-row-in"
        style={{
          background: '#ffffff',
          borderBottom: '1px solid rgba(15, 45, 31, 0.06)',
          animationDelay: `${stagger * 28}ms`,
        }}
      >
        <div
          className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full transition-opacity"
          style={{
            background: meta.accent,
            opacity: g.hasUnread ? 1 : 0,
          }}
        />

        <div
          className="relative flex gap-2.5 px-3 py-3 transition-colors"
          style={{ opacity: g.hasUnread ? 1 : 0.95 }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLDivElement).style.background = '#f5faf7';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLDivElement).style.background = 'transparent';
          }}
        >
          <div
            className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
            style={{
              background: meta.tileBg,
              color: meta.tileFg,
              boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
            }}
          >
            <Icon size={16} strokeWidth={2.25} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="relative">
              <button
                onClick={() => handleOpen(g.task_id, g.latest.id)}
                className="text-left w-full min-w-0 block"
                style={{ background: 'transparent', border: 0, padding: 0, cursor: 'pointer' }}
              >
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span
                    className="text-[13.5px] leading-snug gt-clamp-2"
                    style={{
                      fontWeight: g.hasUnread ? 700 : 600,
                      color: '#0f172a',
                      letterSpacing: '-0.005em',
                      flex: '1 1 100%',
                      minWidth: 0,
                    }}
                  >
                    {g.latest.title}
                  </span>
                  <span
                    className="shrink-0 text-[10px] font-semibold px-2 py-[2px] rounded-full uppercase"
                    style={{
                      background: meta.tileBg,
                      color: meta.tileFg,
                      letterSpacing: '0.04em',
                    }}
                  >
                    {meta.label}
                  </span>
                  {count > 1 && (
                    <span
                      className="shrink-0 text-[10px] font-bold px-2 py-[2px] rounded-full"
                      style={{
                        background: 'var(--gt-surface-3, #e5e7eb)',
                        color: 'var(--gt-text-secondary, #4b5563)',
                      }}
                    >
                      ×{count}
                    </span>
                  )}
                  {g.hasUnread && (
                    <span className="shrink-0 relative inline-flex h-1.5 w-1.5">
                      <span
                        className="absolute inline-flex h-full w-full rounded-full animate-ping"
                        style={{ background: meta.accent, opacity: 0.45 }}
                      />
                      <span
                        className="relative inline-flex h-1.5 w-1.5 rounded-full"
                        style={{ background: meta.accent }}
                      />
                    </span>
                  )}
                </div>
                <p
                  className="text-[12.5px] mt-1 gt-clamp-3"
                  style={{ color: '#374151', lineHeight: '1.5', fontWeight: 450 }}
                >
                  {g.latest.body}
                </p>
                <span
                  className="text-[11.5px] mt-1.5 inline-flex items-center gap-1.5"
                  style={{ color: '#475569', fontStyle: 'italic', fontWeight: 500 }}
                >
                  <Clock size={11} strokeWidth={2} />
                  {formatTime(g.latest.created_at)}
                </span>
              </button>

              <div
                className="absolute top-0 right-0 flex items-center gap-0.5 opacity-0 group-hover/n:opacity-100 transition-opacity rounded-lg"
                style={{
                  background: 'rgba(255,255,255,0.94)',
                  backdropFilter: 'blur(4px)',
                  boxShadow: '0 1px 4px rgba(15, 45, 31, 0.10)',
                  padding: 2,
                }}
              >
                {count > 1 && (
                  <ActionButton
                    title={isExpanded ? 'Collapse' : 'Expand'}
                    onClick={() => toggleExpand(g.task_id)}
                  >
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    />
                  </ActionButton>
                )}
                <ActionButton
                  title={muted ? 'Unmute task' : 'Mute task'}
                  onClick={() => handleToggleMute(g.task_id)}
                  active={muted}
                >
                  {muted ? <BellOff size={14} /> : <Bell size={14} />}
                </ActionButton>
                {g.hasUnread && (
                  <ActionButton
                    title="Mark read"
                    onClick={() => g.items.forEach((n) => !n.is_read && handleMarkRead(n.id))}
                  >
                    <Check size={14} />
                  </ActionButton>
                )}
                <ActionButton
                  title="Open task"
                  onClick={() => handleOpen(g.task_id, g.latest.id)}
                >
                  <ExternalLink size={14} />
                </ActionButton>
                <ActionButton
                  title={count > 1 ? `Delete all ${count} notifications` : 'Delete notification'}
                  onClick={() => handleDeleteGroup(g)}
                  danger
                >
                  <Trash2 size={14} />
                </ActionButton>
              </div>
            </div>
          </div>
        </div>

        {isExpanded && count > 1 && (
          <div
            className="relative pl-[42px] pr-3 pb-3 pt-1 space-y-1"
            style={{ background: '#fafdfb' }}
          >
            <div
              className="absolute top-0 bottom-3 w-px"
              style={{
                left: 22,
                background:
                  'linear-gradient(to bottom, var(--gt-border, rgba(0,0,0,0.08)) 0%, var(--gt-border, rgba(0,0,0,0.08)) 70%, transparent 100%)',
              }}
            />
            {g.items.slice(1).map((n) => {
              const m = metaFor(n.kind);
              return (
                <div
                  key={n.id}
                  className="group/sub relative flex items-start gap-2 py-1.5 px-2 rounded-xl transition-colors"
                  style={{ background: 'transparent' }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background = '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                  }}
                >
                  <span
                    className="shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full"
                    style={{ background: m.accent }}
                  />
                  <button
                    onClick={() => handleOpen(g.task_id, n.id)}
                    className="flex flex-col gap-1 flex-1 min-w-0 text-left"
                    style={{ background: 'transparent', border: 0, padding: 0, cursor: 'pointer' }}
                  >
                    <div className="flex items-center gap-1.5">
                      <span
                        className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase"
                        style={{
                          background: m.tileBg,
                          color: m.tileFg,
                          letterSpacing: '0.04em',
                        }}
                      >
                        {m.label}
                      </span>
                      <span
                        className="shrink-0 text-[10.5px]"
                        style={{
                          color: '#475569',
                          fontStyle: 'italic',
                          fontWeight: 500,
                        }}
                      >
                        {formatTime(n.created_at)}
                      </span>
                    </div>
                    <span
                      className="text-[12.5px] gt-clamp-2"
                      style={{
                        color: '#374151',
                        lineHeight: '1.5',
                        fontWeight: 450,
                      }}
                    >
                      {n.body || n.title}
                    </span>
                  </button>
                  <button
                    onClick={() => handleDelete(n.id)}
                    title="Delete"
                    className="p-1 rounded-md transition-opacity opacity-0 group-hover/sub:opacity-100"
                    style={{
                      color: 'var(--gt-text-tertiary, #6b7280)',
                      background: 'transparent',
                      border: 0,
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      const el = e.currentTarget as HTMLButtonElement;
                      el.style.background = 'var(--gt-prio-urgent-bg, #fee2e2)';
                      el.style.color = 'var(--gt-prio-urgent-fg, #991b1b)';
                    }}
                    onMouseLeave={(e) => {
                      const el = e.currentTarget as HTMLButtonElement;
                      el.style.background = 'transparent';
                      el.style.color = 'var(--gt-text-tertiary, #6b7280)';
                    }}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderBucket = (label: string, list: TaskGroup[]) => {
    if (list.length === 0) return null;
    return (
      <div key={label}>
        <div
          className="sticky top-0 z-10 px-4 py-2 flex items-baseline gap-2.5"
          style={{
            background: '#f5f9f7',
            borderBottom: '1px solid rgba(15, 45, 31, 0.06)',
          }}
        >
          <span
            className="text-[10.5px] font-extrabold uppercase"
            style={{
              color: '#0f3a2a',
              letterSpacing: '0.12em',
            }}
          >
            {label}
          </span>
          <span
            className="text-[11px]"
            style={{ color: '#475569', fontStyle: 'italic', fontWeight: 500 }}
          >
            {list.length} {list.length === 1 ? 'item' : 'items'}
          </span>
        </div>
        {list.map(renderGroup)}
      </div>
    );
  };

  const PANEL_BG = '#ffffff';

  return (
    <>
      <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className="gt-bell-button"
            style={{
              position: 'relative',
              background: 'transparent',
              border: 0,
              padding: 6,
              cursor: 'pointer',
              color: 'inherit',
            }}
            aria-label="Task notifications"
          >
            <Bell size={16} strokeWidth={2.25} className={animating ? 'animate-bell-shake' : ''} />
            {unreadCount > 0 && (
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  minWidth: 16,
                  height: 16,
                  padding: '0 4px',
                  borderRadius: 8,
                  background: 'var(--gc-primary, #1f4d3f)',
                  color: 'white',
                  fontSize: 10,
                  lineHeight: '16px',
                  fontWeight: 600,
                  textAlign: 'center',
                }}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
            {unreadCount > 0 && animating && (
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  minWidth: 16,
                  height: 16,
                  borderRadius: 8,
                  background: 'var(--gc-primary, #1f4d3f)',
                  opacity: 0.45,
                }}
                className="animate-ping"
              />
            )}
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            side="right"
            align="start"
            sideOffset={8}
            collisionPadding={12}
            style={{
              width: 380,
              maxWidth: 'calc(100vw - 24px)',
              maxHeight: 'min(620px, 85vh)',
              background: PANEL_BG,
              border: '1px solid rgba(15, 45, 31, 0.10)',
              borderRadius: 16,
              boxShadow: '0 16px 40px rgba(6, 24, 18, 0.18)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              zIndex: 50,
            }}
          >
            <div
              className="relative px-5 py-4"
              style={{
                background: PANEL_BG,
                borderBottom: '1px solid rgba(15, 45, 31, 0.08)',
              }}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-2xl flex items-center justify-center"
                    style={{
                      background: '#e8f3ed',
                      border: '1px solid rgba(15, 45, 31, 0.10)',
                      color: 'var(--gt-primary, #1f4d3f)',
                    }}
                  >
                    <Bell size={18} strokeWidth={2.25} />
                  </div>
                  <div>
                    <h2
                      className="text-[16px] font-bold leading-tight"
                      style={{
                        color: '#0c2a1f',
                        letterSpacing: '-0.018em',
                      }}
                    >
                      Notifications
                    </h2>
                    <p
                      className="text-[11.5px] mt-0.5"
                      style={{
                        color: '#1f4d3f',
                        fontStyle: 'italic',
                        fontWeight: 500,
                        opacity: 0.85,
                      }}
                    >
                      {unreadCount === 0
                        ? 'All caught up'
                        : `${unreadCount} unread ${unreadCount === 1 ? 'item' : 'items'}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={toggleSound}
                    title={soundOn ? 'Mute sound' : 'Enable sound'}
                    className="p-2 rounded-xl"
                    style={{
                      color: 'rgba(15, 45, 31, 0.72)',
                      background: 'transparent',
                      border: 0,
                      cursor: 'pointer',
                      transition: 'background 120ms, color 120ms',
                    }}
                    onMouseEnter={(e) => {
                      const el = e.currentTarget as HTMLButtonElement;
                      el.style.background = 'rgba(15, 45, 31, 0.08)';
                      el.style.color = 'var(--gt-text-primary, #0e2a1f)';
                    }}
                    onMouseLeave={(e) => {
                      const el = e.currentTarget as HTMLButtonElement;
                      el.style.background = 'transparent';
                      el.style.color = 'rgba(15, 45, 31, 0.72)';
                    }}
                  >
                    {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
                  </button>
                  <Popover.Close asChild>
                    <button
                      title="Close"
                      className="p-2 rounded-xl"
                      style={{
                        color: 'rgba(15, 45, 31, 0.72)',
                        background: 'transparent',
                        border: 0,
                        cursor: 'pointer',
                        transition: 'background 120ms, color 120ms',
                      }}
                      onMouseEnter={(e) => {
                        const el = e.currentTarget as HTMLButtonElement;
                        el.style.background = 'rgba(15, 45, 31, 0.08)';
                        el.style.color = 'var(--gt-text-primary, #0e2a1f)';
                      }}
                      onMouseLeave={(e) => {
                        const el = e.currentTarget as HTMLButtonElement;
                        el.style.background = 'transparent';
                        el.style.color = 'rgba(15, 45, 31, 0.72)';
                      }}
                    >
                      <X size={16} />
                    </button>
                  </Popover.Close>
                </div>
              </div>

              <div
                className="relative inline-flex mt-3 p-1 rounded-full"
                style={{
                  background: 'rgba(15, 45, 31, 0.10)',
                  border: '1px solid rgba(15, 45, 31, 0.08)',
                }}
              >
                <span
                  aria-hidden
                  className="absolute top-1 bottom-1 rounded-full"
                  style={{
                    background: '#ffffff',
                    width: 'calc(50% - 4px)',
                    left: 4,
                    transform: viewMode === 'all' ? 'translateX(100%)' : 'translateX(0)',
                    transition: 'transform 320ms cubic-bezier(0.33, 1, 0.68, 1)',
                    boxShadow: '0 1px 3px rgba(15, 45, 31, 0.18)',
                  }}
                />
                <button
                  onClick={() => changeViewMode('unread')}
                  className="relative z-10 px-4 py-1 text-[11px] font-semibold rounded-full min-w-[88px]"
                  style={{
                    color:
                      viewMode === 'unread'
                        ? 'var(--gt-primary, #1f4d3f)'
                        : 'rgba(15, 45, 31, 0.55)',
                    letterSpacing: '0.02em',
                    transition: 'color 220ms cubic-bezier(0.33, 1, 0.68, 1)',
                    background: 'transparent',
                    border: 0,
                    cursor: 'pointer',
                  }}
                >
                  Unread{unreadCount > 0 ? ` · ${unreadCount}` : ''}
                </button>
                <button
                  onClick={() => changeViewMode('all')}
                  className="relative z-10 px-4 py-1 text-[11px] font-semibold rounded-full min-w-[88px]"
                  style={{
                    color:
                      viewMode === 'all'
                        ? 'var(--gt-primary, #1f4d3f)'
                        : 'rgba(15, 45, 31, 0.55)',
                    letterSpacing: '0.02em',
                    transition: 'color 220ms cubic-bezier(0.33, 1, 0.68, 1)',
                    background: 'transparent',
                    border: 0,
                    cursor: 'pointer',
                  }}
                >
                  All
                </button>
              </div>
            </div>

            <div
              className="overflow-y-auto relative"
              style={{
                height: bodyHeight !== null ? `${bodyHeight}px` : undefined,
                transition: 'height 360ms cubic-bezier(0.33, 1, 0.68, 1)',
                background: PANEL_BG,
              }}
            >
              {loading && hydrated && (
                <div
                  aria-hidden
                  className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden z-20 pointer-events-none"
                  style={{ background: 'transparent' }}
                >
                  <span
                    className="absolute inset-y-0 left-0 w-1/3 gt-refresh-bar"
                    style={{ background: 'var(--gt-primary, #1f4d3f)' }}
                  />
                </div>
              )}
              <div ref={bodyInnerRef}>
                {loading && !hydrated ? (
                  <div className="flex justify-center items-center py-10">
                    <div
                      className="animate-spin rounded-full h-7 w-7"
                      style={{
                        borderTop: '2px solid var(--gt-primary, #1f4d3f)',
                        borderRight: '2px solid var(--gt-primary, #1f4d3f)',
                        borderBottom: '2px solid transparent',
                        borderLeft: '2px solid transparent',
                      }}
                    />
                  </div>
                ) : groups.length === 0 ? (
                  <div className="text-center py-14 px-6">
                    <div
                      className="relative w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center"
                      style={{
                        background: '#e8f3ed',
                        color: 'var(--gt-primary, #1f4d3f)',
                      }}
                    >
                      <Bell size={22} strokeWidth={2} />
                    </div>
                    <p
                      className="text-[13.5px] font-bold"
                      style={{
                        color: 'var(--gt-text-primary, #0e2a1f)',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {viewMode === 'unread' ? 'Nothing unread' : 'No notifications yet'}
                    </p>
                    <p
                      className="text-[12px] mt-1"
                      style={{
                        color: 'rgba(15, 45, 31, 0.55)',
                        fontStyle: 'italic',
                      }}
                    >
                      {viewMode === 'unread'
                        ? "You're all caught up."
                        : 'Activity will show up here.'}
                    </p>
                    {viewMode === 'unread' && (
                      <button
                        onClick={() => changeViewMode('all')}
                        className="text-[12px] font-semibold mt-4 px-4 py-1.5 rounded-full"
                        style={{
                          background: 'var(--gt-primary, #1f4d3f)',
                          color: '#ffffff',
                          border: 0,
                          cursor: 'pointer',
                        }}
                      >
                        View all notifications
                      </button>
                    )}
                  </div>
                ) : (
                  <div key={viewMode} className="gt-list-in">
                    {renderBucket('Today', bucketed.today)}
                    {renderBucket('Yesterday', bucketed.yesterday)}
                    {renderBucket('Earlier', bucketed.earlier)}
                  </div>
                )}
              </div>
            </div>

            {groups.length > 0 && (
              <div
                className="px-4 py-2.5 flex items-center justify-between gap-2"
                style={{
                  background: PANEL_BG,
                  borderTop: '1px solid rgba(15, 45, 31, 0.08)',
                }}
              >
                <button
                  onClick={handleClearAll}
                  className="px-3 py-1.5 text-[12px] font-semibold rounded-full inline-flex items-center gap-2"
                  style={{
                    color: 'var(--gt-prio-urgent-fg, #991b1b)',
                    background: 'transparent',
                    border: 0,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background =
                      'var(--gt-prio-urgent-bg, #fee2e2)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  <Trash2 size={13} />
                  Clear all
                </button>
                {unreadCount > 0 && (
                  <button
                    onClick={async () => {
                      await handleMarkAllRead();
                    }}
                    className="px-4 py-1.5 text-[12px] font-semibold rounded-full inline-flex items-center gap-2"
                    style={{
                      background: 'var(--gt-primary, #1f4d3f)',
                      color: '#ffffff',
                      border: 0,
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(31, 77, 63, 0.24)',
                    }}
                  >
                    <CheckCheck size={14} />
                    Mark all as read
                  </button>
                )}
              </div>
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      <style>{`
        @keyframes bell-shake {
          0%, 100% { transform: rotate(0deg); }
          15% { transform: rotate(12deg); }
          30% { transform: rotate(-10deg); }
          45% { transform: rotate(8deg); }
          60% { transform: rotate(-6deg); }
          75% { transform: rotate(3deg); }
        }
        .animate-bell-shake { animation: bell-shake 0.9s ease-in-out; transform-origin: top center; }
        .gt-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .gt-clamp-3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }

        @keyframes gt-fade-up {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes gt-fade-up-row {
          from { opacity: 0; transform: translateY(6px) scale(0.985); }
          to   { opacity: 1; transform: translateY(0)  scale(1); }
        }
        .gt-list-in { animation: gt-fade-up 220ms cubic-bezier(0.33, 1, 0.68, 1) both; }
        .gt-row-in  { animation: gt-fade-up-row 320ms cubic-bezier(0.33, 1, 0.68, 1) both; will-change: opacity, transform; }

        @keyframes gt-refresh-sweep {
          0%   { transform: translateX(-100%); }
          50%  { transform: translateX(150%); }
          100% { transform: translateX(150%); }
        }
        .gt-refresh-bar { animation: gt-refresh-sweep 1.1s cubic-bezier(0.4, 0, 0.2, 1) infinite; }

        @media (prefers-reduced-motion: reduce) {
          .gt-list-in, .gt-row-in, .gt-refresh-bar, .animate-bell-shake { animation: none !important; }
        }
      `}</style>
    </>
  );
}

export default TaskNotificationsBell;
